/**

 * MPL-10 Activities vault — forms, readings, guides, and media stored under

 * ML_01-Mentor Learning Program / Activities on the Mentology Vault (M:).

 */



import { VAULT_M_ROOT } from './programKnowledgeRegistry';



export const ACTIVITIES_FOLDER_NAME = 'Activities';



export const ML_ACTIVITIES_ROOT =

  `${VAULT_M_ROOT}\\Andre-Library\\Andre-Knowledge\\Classroom-Learning-Material\\ML_01-Mentor Learning Program\\${ACTIVITIES_FOLDER_NAME}`;



export const LOCAL_ACTIVITIES_MIRROR_ROOT =

  `C:\\Mentology_Classroom\\vault-mirror\\Andre-Library\\Andre-Knowledge\\Classroom-Learning-Material\\ML_01-Mentor Learning Program\\${ACTIVITIES_FOLDER_NAME}`;



export type ClassroomActivityKind =

  | 'form'

  | 'reading'

  | 'guide'

  | 'lab'

  | 'media'

  | 'checklist'

  | 'certificate'

  | 'enrollment';



export type ClassroomActivityDelivery = 'embed' | 'vault' | 'student-certificate' | 'held';



export interface ClassroomActivityAsset {

  id: string;

  title: string;

  kind: ClassroomActivityKind;

  fileStem: string;

  extension?: string;

  /** Google Drive / Form URL when known — otherwise resolved from vault file. */

  url?: string | null;

  delivery?: ClassroomActivityDelivery;

}



/** Linked vault activities shown in each module's Activities Box (ordered). */

export const MODULE_LINKED_ACTIVITY_IDS: Readonly<Record<string, readonly string[]>> = {

  '01': ['mentoring-commitment', 'reflective-journal-briefing'],

  '02': ['your-mentoring-experiences', 'mdmm-model-jpg', 'mdmm-model-mp4'],

  '03': ['mentor-self-assessment'],

  '04': ['mentor-guide', 'mentee-guide'],

  '05': ['socratic-inquiry-lab', 'reading-1-1-socratic-method', 'reading-2-4-expert-trap'],

  '06': ['reading-1-5-business-bubble', 'reading-2-5-reciprocity'],

  '07': ['reading-1-6-data-driven-mirror', 'reading-2-6-accountability-paradox'],

  '08': ['reading-1-3-power-of-the-set', 'reading-2-7-observation-blockers', 'action-learning-video-lab'],

  '09': ['certified-mentor-checklist'],

};



export const CERTIFICATE_ACTIVITY_ID = 'certificate-of-completion';

export const ENROLMENT_ACTIVITY_ID = 'program-enrolment-form';

/** Never expose in module Activities — Certificate of Completion is Master Console email only. */
export const STUDENT_ACTIVITY_BLOCKLIST = new Set([
  CERTIFICATE_ACTIVITY_ID,
  ENROLMENT_ACTIVITY_ID,
]);

export function isStudentFacingModuleActivity(activityId: string): boolean {
  return !STUDENT_ACTIVITY_BLOCKLIST.has(String(activityId ?? '').trim());
}



/** Canonical catalogue of MPL-10 activity artefacts in the Activities vault folder. */

export const CLASSROOM_ACTIVITY_ASSETS: readonly ClassroomActivityAsset[] = [

  {

    id: 'mentoring-commitment',

    title: 'Mentoring Commitment',

    kind: 'form',

    fileStem: 'Mentoring Commitment',

    url: 'https://forms.gle/cmBJeg3b7a8W8ZCZ8',

    delivery: 'embed',

  },

  {

    id: 'reflective-journal-briefing',

    title: 'Mentology Reflective Journal Briefing',

    kind: 'guide',

    fileStem: 'Mentology Reflective Journal Briefing',

    delivery: 'vault',

  },

  {

    id: 'your-mentoring-experiences',

    title: 'Your Mentoring Experiences',

    kind: 'form',

    fileStem: 'Your Mentoring Experiences',

    url: 'https://docs.google.com/document/d/1U0gmh6gs7RButgl-XjFd0EU6uCIsVxbrJ6Br_vB7UTM/edit',

    delivery: 'embed',

  },

  {

    id: 'mentor-self-assessment',

    title: 'Mentor Self-Assessment',

    kind: 'form',

    fileStem: 'Mentor Self-Assessment',

    url: 'https://forms.gle/obCJXwZa3xJshYoX6',

    delivery: 'embed',

  },

  {

    id: 'mentor-guide',

    title: 'Mentor Guide',

    kind: 'guide',

    fileStem: 'Mentor Guide',

    url: 'https://drive.google.com/file/d/1mKh7DE4P5NAUM64R4uRPAQaQCmtpceu1/view',

    delivery: 'embed',

  },

  {

    id: 'mentee-guide',

    title: 'Mentee Guide',

    kind: 'guide',

    fileStem: 'Mentee Guide',

    delivery: 'vault',

  },

  {

    id: 'socratic-inquiry-lab',

    title: 'The Socratic Inquiry Lab',

    kind: 'lab',

    fileStem: 'The Socratic Inquiry Lab',

    delivery: 'vault',

  },

  {

    id: 'reading-1-1-socratic-method',

    title: 'Reading 1-1: The Socratic method for Mentors',

    kind: 'reading',

    fileStem: 'Reading 1-1 - The Socratic method for Mentors',

    delivery: 'vault',

  },

  {

    id: 'reading-2-4-expert-trap',

    title: 'Reading 2-4: The Expert Trap (Wiseman, 2017)',

    kind: 'reading',

    fileStem: 'Reading 2-4 - The Expert Trap (Wiseman, 2017)',

    delivery: 'vault',

  },

  {

    id: 'reading-1-5-business-bubble',

    title: 'Reading 1-5: The Business Bubble (Strauss et al., 2026)',

    kind: 'reading',

    fileStem: 'Reading 1-5 - The Business Bubble (Strauss et al., 2026)',

    delivery: 'vault',

  },

  {

    id: 'reading-2-5-reciprocity',

    title: 'Reading 2-5: Reciprocity in Leadership',

    kind: 'reading',

    fileStem: 'Reading 2-5 - Reciprocity in Leadership',

    delivery: 'vault',

  },

  {

    id: 'reading-1-6-data-driven-mirror',

    title: 'Reading 1.6: The Data-Driven Mirror',

    kind: 'reading',

    fileStem: 'Reading 1.6 - The Data-Driven Mirror',

    delivery: 'vault',

  },

  {

    id: 'reading-2-6-accountability-paradox',

    title: 'Reading 2.6: The Accountability Paradox',

    kind: 'reading',

    fileStem: 'Reading 2.6 - The Accountability Paradox',

    delivery: 'vault',

  },

  {

    id: 'reading-1-3-power-of-the-set',

    title: 'Reading 1-3: The Power of the Set',

    kind: 'reading',

    fileStem: 'Reading 1-3 - The Power of the Set',

    delivery: 'vault',

  },

  {

    id: 'reading-2-7-observation-blockers',

    title: 'Reading 2.7: Observation & Emotional Blockers',

    kind: 'reading',

    fileStem: 'Reading 2.7 - Observation & Emotional Blockers',

    delivery: 'vault',

  },

  {

    id: 'action-learning-video-lab',

    title: 'Action Learning Analysis - The Video Lab: What is Action Learning',

    kind: 'lab',

    fileStem: 'Action Learning Analysis - The Video Lab - What is Action Learning',

    url: 'https://www.youtube.com/watch?v=x8gjjN0pu5o',

    delivery: 'embed',

  },

  {

    id: 'mdmm-model-jpg',

    title: 'Multi-Dimensional Mentoring Model (jpg)',

    kind: 'media',

    fileStem: 'Multi-Dimensional Mentoring Model',

    extension: 'jpg',

    delivery: 'vault',

  },

  {

    id: 'mdmm-model-mp4',

    title: 'Multi-Dimensional Mentoring Model (mp4)',

    kind: 'media',

    fileStem: 'Multi-Dimensional Mentoring Model',

    extension: 'mp4',

    delivery: 'vault',

  },

  {

    id: 'certified-mentor-checklist',

    title: 'Certified Mentology Mentor Check List',

    kind: 'checklist',

    fileStem: 'Certified Mentology Mentor Check List',

    delivery: 'vault',

  },

  // Master Console email issue only — never shown in module Activities buttons.

  {

    id: 'certificate-of-completion',

    title: 'Certificate of Completion',

    kind: 'certificate',

    fileStem: 'Certificate of Completion',

    delivery: 'student-certificate',

  },

  {

    id: 'program-enrolment-form',

    title: 'Program Enrolment Form',

    kind: 'enrollment',

    fileStem: 'Program Enrolment Form',

    delivery: 'held',

  },

] as const;



export function resolveActivityFileName(asset: ClassroomActivityAsset): string {

  return asset.extension ? `${asset.fileStem}.${asset.extension}` : asset.fileStem;

}



export function resolveActivityVaultPath(asset: ClassroomActivityAsset): string {

  return `${ML_ACTIVITIES_ROOT}\\${resolveActivityFileName(asset)}`;

}



export function resolveActivityLocalMirrorPath(asset: ClassroomActivityAsset): string {

  return `${LOCAL_ACTIVITIES_MIRROR_ROOT}\\${resolveActivityFileName(asset)}`;

}



export function getClassroomActivityById(id: string): ClassroomActivityAsset | undefined {

  return CLASSROOM_ACTIVITY_ASSETS.find((asset) => asset.id === id);

}



export function listModuleLinkedActivities(moduleId: string): ClassroomActivityAsset[] {

  const ids = MODULE_LINKED_ACTIVITY_IDS[moduleId] ?? [];

  return ids

    .map((id) => getClassroomActivityById(id))

    .filter((asset): asset is ClassroomActivityAsset => Boolean(asset && isStudentFacingModuleActivity(asset.id)));

}



export function listClassroomActivities(moduleId?: string): ClassroomActivityAsset[] {

  if (!moduleId) {

    return [...CLASSROOM_ACTIVITY_ASSETS];

  }

  return listModuleLinkedActivities(moduleId);

}



export function isActivityHeld(asset: ClassroomActivityAsset): boolean {

  return asset.delivery === 'held';

}



export function isActivityOpenable(asset: ClassroomActivityAsset): boolean {

  return asset.delivery !== 'held';

}

export interface SerializedClassroomActivity {
  id: string;
  title: string;
  kind: ClassroomActivityKind;
  fileName: string;
  vaultPath: string;
  localMirrorPath: string;
  url: string | null;
  delivery: ClassroomActivityDelivery;
  openable: boolean;
  resolved?: boolean;
}

export function serializeClassroomActivityForClient(asset: ClassroomActivityAsset): SerializedClassroomActivity {
  return {
    id: asset.id,
    title: asset.title,
    kind: asset.kind,
    fileName: resolveActivityFileName(asset),
    vaultPath: resolveActivityVaultPath(asset),
    localMirrorPath: resolveActivityLocalMirrorPath(asset),
    url: asset.url ?? null,
    delivery: asset.delivery ?? 'vault',
    openable: isActivityOpenable(asset),
  };
}

export function serializeResourceLinkForClient(link: {
  id: string;
  label: string;
  path: string;
}): SerializedClassroomActivity | null {
  const asset = getClassroomActivityById(link.id);
  const path = link.path?.trim();

  if (asset) {
    const base = serializeClassroomActivityForClient(asset);
    return {
      ...base,
      title: link.label?.trim() || base.title,
      url: path?.startsWith('http') ? path : base.url,
      openable: path?.startsWith('http') ? true : base.openable,
      resolved: path?.startsWith('http') ? true : base.resolved ?? Boolean(base.url),
    };
  }

  if (path?.startsWith('http')) {
    return {
      id: link.id,
      title: link.label?.trim() || 'Linked document',
      kind: 'reading',
      fileName: link.label?.trim() || link.id,
      vaultPath: '',
      localMirrorPath: '',
      url: path,
      delivery: 'embed',
      openable: true,
      resolved: true,
    };
  }

  if (path) {
    const fileName = path.split(/[/\\]/).pop() || link.id;
    return {
      id: link.id,
      title: link.label?.trim() || fileName.replace(/\.[^.]+$/, '') || fileName,
      kind: 'media',
      fileName,
      vaultPath: path,
      localMirrorPath: '',
      url: null,
      delivery: 'vault',
      openable: true,
      resolved: true,
    };
  }

  return null;
}
